import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery } from '@apollo/client';
import {
  Button, Checkbox, FieldGroup, PaymentIcon, InputField, ErrorMessage, DropDown,
} from 'anf-core-react';
import CreditCardInput from '../Common/CreditCardInput/CreditCardInput';
import ExpiryField from './ExpiryField';
import CVVField from './CVVField';
import CountrySelector from './CountrySelector';
import { TmntText, LegalModal } from '../Common';
import { FormGroup, FormCell } from '../FormStructure';
import { useLegalLinkEvents } from '../../hooks';
import { ModalContextProvider } from '../../context/ModalContext';

import {
  UPDATE_ORDER_PAYMENT,
  COUNTRY_LIST_QUERY,
  WSS_PAYMENT_FORM_ERROR_MESSAGES_QUERY,
  STATE_LIST_QUERY,
} from './operations';
import { ccRegexArray } from '../Payments/paymentConstants';
import {
  getCardBrand,
  formatExpirationDate,
  isCardNumberInvalid,
  isExpDateInvalid,
  isCvvInvalid,
  findBadParams,
} from '../../tools/CreditCardHelper/CreditCardHelper';
import { getCvvImageUrl } from '../../tools/CreditCardHelper/getCvvImageUrl';
import {
  getInvalidCardFieldsFromBadParams,
  handlePaymentUpdateCompleted,
  INVALID_CREDIT_CARD_FIELDS,
} from './WssPaymentUpdateForm.logic';

function WssPaymentUpdateForm({
  orderNumber, wssPaymentDetails, onPaymentError, onPaymentSuccess, handlePaymentUpdateCompletedOverride,
}) {
  const { data: countryData } = useQuery(COUNTRY_LIST_QUERY);
  const { data: errorMessagesData } = useQuery(WSS_PAYMENT_FORM_ERROR_MESSAGES_QUERY);

  // Create country list from either the new query or existing data
  let countryList = [];
  if (countryData?.countryList) {
    countryList = countryData.countryList;
  } else if (wssPaymentDetails?.billingCountryList) {
    countryList = wssPaymentDetails.billingCountryList.map((country) => ({
      value: country.value,
      label: country.label?.value || country.label || country.value,
    }));
  }

  // Get error messages with TMNT support
  const errorMessages = errorMessagesData?.wssPaymentFormErrorMessages || {};

  // State for shipping address validation - will be updated when wssPaymentDetails changes
  const [hasValidShippingAddress, setHasValidShippingAddress] = useState(false);
  const [useShippingAddress, setUseShippingAddress] = useState(false);
  const [cardDetails, setCardDetails] = useState({
    cardNumber: '',
    expirationDate: '',
    securityCode: '',
    cardBrand: '',
    formattedNumber: '',
    code: '021', // Default card code
  });
  const [billingAddress, setBillingAddress] = useState({
    country: '', // Start with empty country to require selection
    firstName: '',
    lastName: '',
    address1: '',
    address2: '',
    city: '',
    state: '', // Will be auto-populated from order data
    postalCode: '',
  });
  const [hasValidationError, setHasValidationError] = useState(INVALID_CREDIT_CARD_FIELDS);

  const legalLinkRef = useRef(null);
  const [legalTmntKey, setLegalTmntKey] = useState(null);
  useLegalLinkEvents(legalLinkRef, (value) => {
    setLegalTmntKey(value);
  });

  // Fetch states for selected country
  const { data: stateListData } = useQuery(STATE_LIST_QUERY, {
    variables: { country: billingAddress.country },
    skip: !billingAddress.country || billingAddress.country === '',
    fetchPolicy: 'cache-first',
  });

  const stateList = stateListData?.stateList || [];

  // Update shipping address validation when wssPaymentDetails changes
  useEffect(() => {
    const shippingAddr = wssPaymentDetails?.shippingAddress;

    // Check if shipping address is complete and allowed for billing
    const isValidAndComplete = shippingAddr
      && shippingAddr.address1
      && shippingAddr.city
      && shippingAddr.country
      && shippingAddr.firstName
      && shippingAddr.lastName;

    // Check if billing is allowed
    const isBillingAllowed = shippingAddr?.isBillingAllowed !== false;

    const canUseBilling = isValidAndComplete && isBillingAllowed;

    setHasValidShippingAddress(!!canUseBilling);

    // Only set useShippingAddress to true if we have a valid shipping address
    if (canUseBilling) {
      setUseShippingAddress(true);
    }
  }, [wssPaymentDetails]);

  // Clear validation errors when switching between saved payment modes
  useEffect(() => {
    setHasValidationError(INVALID_CREDIT_CARD_FIELDS);
  }, []);

  const [updateOrderPayment, { loading }] = useMutation(UPDATE_ORDER_PAYMENT, {
    context: { batch: true },
    onCompleted: (data) => {
      (handlePaymentUpdateCompletedOverride || handlePaymentUpdateCompleted)(
        data,
        setHasValidationError,
        setCardDetails,
        setBillingAddress,
        onPaymentSuccess,
        onPaymentError,
      );
    },
    onError: (error) => {
      // Extract validation errors from GraphQL error
      const badParams = findBadParams(error.graphQLErrors || []);
      const invalidCardFields = getInvalidCardFieldsFromBadParams(badParams);
      setHasValidationError(invalidCardFields);
      if (onPaymentError) {
        // Use enhanced error message with customer service contact info if available
        let errorMessage = error.graphQLErrors?.[0]?.message || 'Payment update failed';

        // If we have a general error message with contact info, use that instead
        if (errorMessages.generalErrorWithContact?.value) {
          errorMessage = errorMessages.generalErrorWithContact.value;
        } else if (errorMessages.customerServiceContactInfo) {
          // Fallback: append customer service contact info to the error message
          errorMessage = `${errorMessage}. Please contact Customer Service: ${errorMessages.customerServiceContactInfo}`;
        }

        onPaymentError(errorMessage);
      }
    },
  });

  const handleExpirationChange = (value) => {
    const formattedValue = formatExpirationDate(value);
    setCardDetails((prev) => ({
      ...prev,
      expirationDate: formattedValue,
    }));

    // Clear error when user starts typing
    if (hasValidationError.cardExpiry || hasValidationError.card) {
      setHasValidationError((prev) => ({
        ...prev,
        cardExpiry: false,
        card: false,
      }));
    }
  };

  const handleCVVChange = (value) => {
    setCardDetails((prev) => ({
      ...prev,
      securityCode: value,
    }));

    // Clear error when user starts typing
    if (hasValidationError.cardCvv || hasValidationError.card) {
      setHasValidationError((prev) => ({
        ...prev,
        cardCvv: false,
        card: false,
      }));
    }
  };

  const handleAddressChange = (field, value) => {
    setBillingAddress((prev) => {
      // If country is changing, clear other fields but auto-populate state from order data
      if (field === 'country') {
        // Try to get state from order data when country is selected
        let autoPopulatedState = '';

        // Try to auto-populate state from order data for any country that has states
        if (value && value !== '') {
          // First try default billing address from order, then fall back to shipping
          const orderBillingState = wssPaymentDetails?.defaultBillingAddress?.state
            || wssPaymentDetails?.defaultBillingAddress?.province;
          const orderShippingState = wssPaymentDetails?.shippingAddress?.state
            || wssPaymentDetails?.shippingAddress?.province;

          autoPopulatedState = orderBillingState || orderShippingState || '';
        }

        return {
          country: value,
          firstName: '',
          lastName: '',
          address1: '',
          address2: '',
          city: '',
          state: autoPopulatedState,
          postalCode: '',
        };
      }
      return {
        ...prev,
        [field]: value,
      };
    });

    // Clear billing address error when user starts typing
    if (hasValidationError.billingAddress) {
      setHasValidationError((prev) => ({ ...prev, billingAddress: false }));
    }
  };

  const getOrderEmail = () => {
    // Try to get email from URL params (common in WSS flow)
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('orderEmail') || urlParams.get('email') || '';
  };

  const validateForm = () => {
    const invalidCardFields = { ...INVALID_CREDIT_CARD_FIELDS };

    // Validate card number
    const cleanCardNumber = cardDetails.cardNumber.replace(/\s/g, '');
    const effectiveCardBrand = cardDetails.cardBrand || getCardBrand(cleanCardNumber, ccRegexArray);
    if (!cleanCardNumber || isCardNumberInvalid(cleanCardNumber, cardDetails.cardBrand)) {
      invalidCardFields.cardNumber = true;
    }

    // Validate expiration date
    if (!cardDetails.expirationDate || isExpDateInvalid(cardDetails.expirationDate.replace(/\//g, ''))) {
      invalidCardFields.cardExpiry = true;
    }

    // Validate CVV: 4 digits for Amex, 3 for others
    if (isCvvInvalid(cardDetails.securityCode, effectiveCardBrand)) {
      invalidCardFields.cardCvv = true;
    }

    // Validate billing address if not using shipping
    if (!useShippingAddress) {
      if (!billingAddress.firstName || !billingAddress.lastName
          || !billingAddress.address1 || !billingAddress.city
          || !billingAddress.postalCode || !billingAddress.country) {
        invalidCardFields.billingAddress = true;
      }

      // Validate state if states are available for the selected country
      if (stateList.length > 0 && !billingAddress.state) {
        invalidCardFields.state = true;
      }
    }

    // If any card field is invalid, mark the whole card as invalid
    if (invalidCardFields.cardNumber || invalidCardFields.cardExpiry || invalidCardFields.cardCvv) {
      invalidCardFields.card = true;
    }

    setHasValidationError(invalidCardFields);
    return Object.values(invalidCardFields).every((error) => !error);
  };

  const handleUpdate = async () => {
    if (!validateForm()) {
      return;
    }

    // Prepare credit card input
    const cleanCardNumber = cardDetails.cardNumber.replace(/\s/g, '');
    const [expMonth, expYear] = cardDetails.expirationDate.split('/');

    const creditCardInput = {
      cardNumber: cleanCardNumber,
      cardBrand: cardDetails.cardBrand || getCardBrand(cleanCardNumber, ccRegexArray),
      cardExpireMonth: expMonth?.padStart(2, '0') || '',
      cardExpireYear: expYear || '',
      cardLastFour: cleanCardNumber.slice(-4),
      cvv: cardDetails.securityCode,
    };

    // Prepare input for mutation
    const input = {
      orderId: orderNumber,
      email: getOrderEmail(),
      creditCard: creditCardInput,
      useShippingAddressForBilling: useShippingAddress,
    };

    // Add billing address if not using shipping
    if (!useShippingAddress && billingAddress) {
      input.billingAddress = {
        address1: billingAddress.address1 || '',
        address2: billingAddress.address2 || '',
        city: billingAddress.city || '',
        state: billingAddress.state || '',
        postalCode: billingAddress.postalCode || billingAddress.zipCode || '',
        country: billingAddress.country || '',
        firstName: billingAddress.firstName || '',
        lastName: billingAddress.lastName || '',
      };
    }

    try {
      await updateOrderPayment({ variables: { input } });
    } catch (error) {
      // Error handling is done in onError callback
    }
  };

  const {
    updateCardInformationTitle,
    billToShippingAddressLabel,
    updateButtonText,
    wssCardLabel,
  } = wssPaymentDetails || {};

  return (
    <div className="wss-payment-update-form" data-testid="wss-payment-update-form">
      <h3 className="h3 WSS-section-title">
        {updateCardInformationTitle?.value || 'Please update your card information'}
      </h3>
      <div className="wss-payment-form-container">
        <div className="wss-form-wrapper">
          <div className="wss-form-inner-wrapper">

            {/* Credit/Debit Card Icon */}
            <FormGroup>
              <FormCell>
                <div className="wss-credit-card-icon-col">
                  <div className="wss-credit-card-tile">
                    <PaymentIcon iconName="default-card" labelText="Credit / Debit Card" />
                  </div>
                  <div className="wss-credit-card-label">
                    <TmntText tmnt={wssCardLabel} />
                  </div>
                </div>
              </FormCell>
            </FormGroup>

            {/* Credit Card Fields - MFE specific classes */}
            <FormGroup>
              <FormCell>
                <div className="wss-credit-card-section">
                  <FieldGroup legend="Credit Card Fields">
                    <CreditCardInput
                      id="wss-credit-card-number"
                      label="Card Number"
                      isInvalid={hasValidationError.cardNumber}
                      cardNumError={
                        hasValidationError.cardNumber
                          ? { key: errorMessages.cardNumberError?.key || 'CARD_NUMBER_ERROR', value: errorMessages.cardNumberError?.value || 'Please enter a valid card number.' }
                          : null
                      }
                      ccRegexArray={ccRegexArray}
                      onChange={(cardInfo) => {
                        setCardDetails((prev) => ({
                          ...prev,
                          cardNumber: cardInfo.cardNumber,
                          cardBrand: cardInfo.cardBrand,
                          formattedNumber: cardInfo.cardNumber,
                        }));
                        if (hasValidationError.cardNumber || hasValidationError.card) {
                          setHasValidationError((prev) => ({
                            ...prev,
                            cardNumber: false,
                            card: false,
                          }));
                        }
                      }}
                    />
                    <div className="expiry-cvv-row">
                      <ExpiryField
                        expiryDate={cardDetails.expirationDate}
                        onChange={handleExpirationChange}
                        label="Expiration Date"
                        errorMessage={
                          hasValidationError.cardExpiry
                            ? errorMessages.expiryError?.value || 'Please enter a valid expiration date.'
                            : ''
                        }
                        hasValidationError={hasValidationError.cardExpiry}
                        index={0}
                      />
                      <CVVField
                        cvv={cardDetails.securityCode}
                        onChange={handleCVVChange}
                        label="Security Code"
                        errorMessage={
                          hasValidationError.cardCvv
                            ? errorMessages.cvvError?.value || 'Please enter a valid security code.'
                            : ''
                        }
                        hasValidationError={hasValidationError.cardCvv}
                        cardBrand={cardDetails.cardBrand || '021'}
                        index={0}
                        tooltipMessage="Security Code Example"
                        tooltipImage={getCvvImageUrl()}
                      />
                    </div>
                    {hasValidationError.card && (
                      <div className="error-message" role="alert">
                        {errorMessages.generalErrorWithContact?.value || 'Unable to process card. Please check your information and try again.'}
                      </div>
                    )}
                  </FieldGroup>
                </div>
              </FormCell>
            </FormGroup>

            {/* Billing Address Section */}
            {hasValidShippingAddress && (
              <div className="wss-form-group" data-display="true" data-transition="false">
                <div className="wss-form-content">
                  <div style={{
                    marginTop: '16px', marginBottom: '16px', display: 'flex', alignItems: 'center',
                  }}
                  >
                    <Checkbox
                      isChecked={useShippingAddress}
                      onChange={() => setUseShippingAddress((prev) => !prev)}
                      description={<span>{billToShippingAddressLabel?.value || 'Bill to shipping address'}</span>}
                      disabled={!hasValidShippingAddress}
                      id="wss-bill-to-shipping-checkbox"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Display shipping address when checkbox is checked and shipping address is available */}
            {useShippingAddress && hasValidShippingAddress && (
              <div className="wss-form-group" data-display="true" data-transition="false">
                <div className="wss-form-content wss-address-display">
                  <div className="wss-shipping-address-display">
                    <p>
                      {wssPaymentDetails.shippingAddress.firstName}
                      {' '}
                      {wssPaymentDetails.shippingAddress.lastName}
                    </p>
                    <p>{wssPaymentDetails.shippingAddress.address1}</p>
                    {wssPaymentDetails.shippingAddress.address2 && <p>{wssPaymentDetails.shippingAddress.address2}</p>}
                    <p>
                      {wssPaymentDetails.shippingAddress.city}
                      {wssPaymentDetails.shippingAddress.state && `, ${wssPaymentDetails.shippingAddress.state}`}
                      {' '}
                      {wssPaymentDetails.shippingAddress.postalCode || wssPaymentDetails.shippingAddress.zipCode}
                    </p>
                    <p>{wssPaymentDetails.shippingAddress.country}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Billing Address Form - Show when not using shipping address OR when no shipping address is available */}
            {(!useShippingAddress || !hasValidShippingAddress) && (
              <div className="wss-form-group" data-display="true" data-transition="false" data-testid="address-form">
                <div className="wss-form-content">
                  <h4 style={{ marginBottom: '8px' }}>Billing Address</h4>
                  <FieldGroup describedBy="Billing Address" legend="Billing Address Information">
                    <div className="country-field">
                      <CountrySelector
                        countryList={countryList}
                        selectedCountry={billingAddress.country}
                        onCountryChange={(newCountry) => handleAddressChange('country', newCountry)}
                        label="Country"
                        isInvalid={hasValidationError.billingAddress}
                        errorMessage={errorMessages.countryError?.value || 'Please select a country'}
                      />
                    </div>

                    {/* Show other fields only after country is selected */}
                    {billingAddress.country && (
                      <>
                        <div className="name-fields-group">
                          <InputField
                            label="First Name"
                            id="wss-billing-firstName"
                            name="firstName"
                            value={billingAddress.firstName || ''}
                            onChange={(e) => handleAddressChange('firstName', e.target.value)}
                            autoComplete="given-name"
                            isInvalid={hasValidationError.billingAddress && !billingAddress.firstName}
                          >
                            {hasValidationError.billingAddress && !billingAddress.firstName && (
                              <ErrorMessage>
                                {errorMessages.firstNameError?.value || 'Please enter a valid first name.'}
                              </ErrorMessage>
                            )}
                          </InputField>

                          <InputField
                            label="Last Name"
                            id="wss-billing-lastName"
                            name="lastName"
                            value={billingAddress.lastName || ''}
                            onChange={(e) => handleAddressChange('lastName', e.target.value)}
                            autoComplete="family-name"
                            isInvalid={hasValidationError.billingAddress && !billingAddress.lastName}
                          >
                            {hasValidationError.billingAddress && !billingAddress.lastName && (
                              <ErrorMessage>
                                {errorMessages.lastNameError?.value || 'Please enter a valid last name.'}
                              </ErrorMessage>
                            )}
                          </InputField>
                        </div>

                        <div className="address-fields-group">
                          <InputField
                            label="Address"
                            id="wss-billing-address1"
                            name="address1"
                            value={billingAddress.address1 || ''}
                            onChange={(e) => handleAddressChange('address1', e.target.value)}
                            autoComplete="street-address"
                            isInvalid={hasValidationError.billingAddress && !billingAddress.address1}
                          >
                            {hasValidationError.billingAddress && !billingAddress.address1 && (
                              <ErrorMessage>
                                {errorMessages.addressError?.value || 'Please enter a valid address.'}
                              </ErrorMessage>
                            )}
                          </InputField>

                          <InputField
                            label="Apt / Bldg / Other"
                            id="wss-billing-address2"
                            name="address2"
                            value={billingAddress.address2 || ''}
                            onChange={(e) => handleAddressChange('address2', e.target.value)}
                            autoComplete="address-line2"
                          />

                          <InputField
                            label="City"
                            id="wss-billing-city"
                            name="city"
                            value={billingAddress.city || ''}
                            onChange={(e) => handleAddressChange('city', e.target.value)}
                            autoComplete="address-level2"
                            isInvalid={hasValidationError.billingAddress && !billingAddress.city}
                          >
                            {hasValidationError.billingAddress && !billingAddress.city && (
                              <ErrorMessage>
                                {errorMessages.cityError?.value || 'Please enter a valid city.'}
                              </ErrorMessage>
                            )}
                          </InputField>

                          {/* State/Province dropdown - only show if states are available for the country */}
                          {stateList.length > 0 && (
                            <DropDown
                              label="State/Province"
                              id="wss-billing-state"
                              name="state"
                              value={billingAddress.state || ''}
                              onChange={(e) => {
                                handleAddressChange('state', e.target.value);
                                // Clear state error when user makes a selection
                                if (hasValidationError.state) {
                                  setHasValidationError((prev) => ({ ...prev, state: false }));
                                }
                              }}
                              options={[
                                { label: 'Select a state/province', value: '' },
                                ...stateList.map((state) => ({
                                  label: state.label?.value || state.id,
                                  value: state.id,
                                })),
                              ]}
                              isInvalid={hasValidationError.state}
                            >
                              {hasValidationError.state && (
                                <ErrorMessage>
                                  {errorMessages.stateError?.value || 'Please select a state.'}
                                </ErrorMessage>
                              )}
                            </DropDown>
                          )}

                          <InputField
                            label="Postal Code"
                            id="wss-billing-postalCode"
                            name="postalCode"
                            value={billingAddress.postalCode || billingAddress.zipCode || ''}
                            onChange={(e) => handleAddressChange('postalCode', e.target.value)}
                            autoComplete="postal-code"
                            isInvalid={hasValidationError.billingAddress && !billingAddress.postalCode}
                          >
                            {hasValidationError.billingAddress && !billingAddress.postalCode && (
                              <ErrorMessage>
                                {errorMessages.postalCodeError?.value || 'Please enter a valid postal code.'}
                              </ErrorMessage>
                            )}
                          </InputField>
                        </div>
                      </>
                    )}
                  </FieldGroup>
                </div>
              </div>
            )}

            {/* Legal Links */}
            {wssPaymentDetails?.termsOfUseText && wssPaymentDetails?.privacyPolicyText && (
              <div className="wss-form-group" data-display="true" data-transition="false">
                <div className="wss-form-content">
                  <hr className="wss-legal-links-separator" />
                  <div className="wss-legal-links" data-testid="legal-text" ref={legalLinkRef}>
                    <button
                      type="button"
                      className="js-legal-policy-modal button-as-link"
                      data-title="Terms of Use"
                      data-textkey="LEGAL_SITE_USE"
                    >
                      <TmntText tmnt={wssPaymentDetails.termsOfUseText} />
                    </button>
                    <span className="legal-separator">|</span>
                    <button
                      type="button"
                      className="js-legal-policy-modal button-as-link"
                      data-title="Privacy Policy"
                      data-textkey="LEGAL_PRIVACY_POLICY"
                    >
                      <TmntText tmnt={wssPaymentDetails.privacyPolicyText} />
                    </button>
                    {legalTmntKey && (
                      <ModalContextProvider>
                        <LegalModal
                          legalText={legalTmntKey}
                          onClose={() => setLegalTmntKey(null)}
                        />
                      </ModalContextProvider>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="wss-form-group" data-display="true" data-transition="false">
              <div className="wss-form-content">
                <Button
                  variant="secondary"
                  isFullWidth
                  className="wss-update-btn"
                  onClick={handleUpdate}
                  disabled={loading}
                >
                  {updateButtonText?.value || 'Update'}
                </Button>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Hidden input for payment code */}
      <input
        name="creditCard[0].paymentCode"
        value={cardDetails.code || '021'}
        type="hidden"
        data-testid="wss-cc-payment-code"
      />
    </div>
  );
}

WssPaymentUpdateForm.propTypes = {
  orderNumber: PropTypes.string,
  wssPaymentDetails: PropTypes.shape({
    grandTotalFormatted: PropTypes.string,
    authAmountFormatted: PropTypes.string,
    currencyPattern: PropTypes.string,
    updateCardInformationTitle: PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.string,
    }),
    billingAddressTitle: PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.string,
    }),
    billToShippingAddressLabel: PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.string,
    }),
    updateButtonText: PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.string,
    }),
    wssCardLabel: PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.string,
    }),
    termsOfUseText: PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.string,
    }),
    privacyPolicyText: PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.string,
    }),
    shippingAddress: PropTypes.shape({
      address1: PropTypes.string,
      address2: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string,
      postalCode: PropTypes.string,
      zipCode: PropTypes.string,
      country: PropTypes.string,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      province: PropTypes.string,
      isBillingAllowed: PropTypes.bool,
      isShippingAllowed: PropTypes.bool,
      isDefaultBilling: PropTypes.bool,
      isDefaultShipping: PropTypes.bool,
    }),
    defaultBillingAddress: PropTypes.shape({
      address1: PropTypes.string,
      address2: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string,
      province: PropTypes.string,
      postalCode: PropTypes.string,
      zipCode: PropTypes.string,
      country: PropTypes.string,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
    }),
    billingCountryList: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.string,
    })),
  }),
  onPaymentError: PropTypes.func,
  onPaymentSuccess: PropTypes.func,
  handlePaymentUpdateCompletedOverride: PropTypes.func,
};

WssPaymentUpdateForm.defaultProps = {
  orderNumber: '',
  wssPaymentDetails: null,
  onPaymentError: undefined,
  onPaymentSuccess: undefined,
  handlePaymentUpdateCompletedOverride: undefined,
};

export default WssPaymentUpdateForm;
